import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useT } from "@/context/LangContext";
import { SECTOR_BY_SLUG } from "@/constants/sectors";

export default function SectorDetail() {
  const { slug } = useParams();
  const t = useT();
  const sector = SECTOR_BY_SLUG[slug];
  if (!sector) return <Navigate to="/secteurs" replace />;

  const Icon = sector.icon;
  const features = ["f1", "f2", "f3", "f4"];

  return (
    <div data-testid={`sector-detail-${slug}`}>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <img src={sector.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative max-w-7xl mx-auto h-full px-6 lg:px-10 pt-28 pb-16 flex flex-col justify-end text-white">
          <Link to="/secteurs" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 hover:text-white transition-colors" data-testid="sector-back">
            <ArrowLeft size={14} /> {t("sectors.back")}
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <Icon size={28} style={{ color: sector.accent }} />
            <p className="kicker !text-[var(--accent)]">{t("sectors.kicker")}</p>
          </div>
          <h1 className="font-display text-5xl lg:text-7xl font-black tracking-tighter mt-4 max-w-4xl">
            {t(`sector.${slug}.t`)}
          </h1>
        </div>
      </section>

      {/* Description + Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <p className="text-xl leading-relaxed text-[var(--ink-soft)]">{t(`sector.${slug}.long`)}</p>
        </div>
        <div className="lg:col-span-5">
          <p className="kicker">{t("sectors.offers")}</p>
          <ul className="mt-6 space-y-4">
            {features.map((f) => (
              <li key={f} className="flex gap-3 items-start" data-testid={`feature-${f}`}>
                <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] shrink-0">
                  <Check size={14} />
                </span>
                <span className="text-[var(--ink)]">{t(`sector.${slug}.${f}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-[var(--bg-soft)] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
            <div className="md:col-span-6">
              <p className="kicker">{t("sectors.gallery")}</p>
              <h2 className="font-display text-4xl lg:text-5xl font-black mt-3">{t(`sector.${slug}.t`)}</h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-[var(--ink-soft)]">{t("sectors.gallery.desc")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {sector.gallery.map((src, i) => (
              <div
                key={i}
                className={`overflow-hidden rounded-lg bg-black/5 ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
                data-testid={`gallery-${slug}-${i}`}
              >
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="bg-[var(--ink)] text-white p-10 lg:p-14 rounded-lg flex flex-col lg:flex-row justify-between items-center gap-8">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold max-w-xl">{t("sectors.cta.title")}</h2>
            <p className="text-white/60 mt-3 max-w-lg">{t("sectors.cta.desc")}</p>
          </div>
          <Link to="/contact" className="btn-primary shrink-0" data-testid="sector-cta">
            {t("home.cta.contact")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
